import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, BedDouble, CircleCheck, CircleDot, LogOut } from "lucide-react";
import { toast } from "sonner";

function InvoiceBadge({ status }) {
  const map = {
    settled: { c: "bg-emerald-100 text-emerald-700", t: "Settled" },
    pending: { c: "bg-amber-100 text-amber-700", t: "Pending" },
  };
  const s = map[status] || { c: "bg-slate-100 text-slate-500", t: "—" };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${s.c}`}>{s.t}</span>;
}

export default function IpdAdmissions() {
  const { activeId, has } = useFacility();
  const navigate = useNavigate();
  const [statusTab, setStatusTab] = useState("admitted");
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/ipd/admissions?facility_id=${activeId}&status=${statusTab}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get(`/ipd/stats?facility_id=${activeId}`).then((r) => setStats(r.data)).catch(() => {});
  }, [activeId, statusTab]);
  useEffect(load, [load]);

  const discharge = async (a) => {
    try { await api.patch(`/ipd/admissions/${a.id}/discharge`); toast.success(`${a.patient_name} discharged`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const discharged = statusTab === "discharged";

  return (
    <div data-testid="ipd-admissions-page">
      <PageHeader title="Admissions" subtitle="Inpatients in this facility — current and discharged."
        actions={has("ipd:create") && <Button onClick={() => navigate("/app/ipd/admit")} className="bg-blue-700 hover:bg-blue-800" data-testid="admissions-admit-button"><Plus className="h-4 w-4 mr-1.5" /> Admit Patient</Button>} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Admitted" value={stats.current_admissions} icon={BedDouble} testId="ipd-adm-stat-admitted" />
          <StatCard label="Occupied Beds" value={stats.occupied} icon={CircleDot} tone="amber" testId="ipd-adm-stat-occupied" />
          <StatCard label="Available Beds" value={stats.available} icon={CircleCheck} tone="emerald" testId="ipd-adm-stat-available" />
          <StatCard label="Occupancy" value={`${stats.occupancy_percent}%`} icon={BedDouble} tone="slate" testId="ipd-adm-stat-occupancy" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3" data-testid="admissions-status-tabs">
        {[["admitted", "Current"], ["discharged", "Discharged"]].map(([k, label]) => (
          <button key={k} onClick={() => setStatusTab(k)} data-testid={`admissions-tab-${k}`}
            className={`text-sm px-4 py-1.5 rounded-full border ${statusTab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>IPD No</TableHead><TableHead>Patient</TableHead><TableHead>Bed</TableHead><TableHead>Department</TableHead><TableHead>Doctor</TableHead>
              <TableHead>Admitted</TableHead>{discharged ? <TableHead>Discharged</TableHead> : null}<TableHead className="text-right">{discharged ? "Invoice" : "Actions"}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`admission-row-${a.id}`} onClick={() => navigate(`/app/ipd/admissions/${a.id}`)}>
                  <TableCell className="font-mono text-xs text-blue-700 font-semibold">{a.ipd_no}</TableCell>
                  <TableCell data-testid={`admission-open-${a.id}`}><div className="font-medium text-slate-800 hover:text-blue-700">{a.patient_name}</div><div className="text-xs text-slate-400 font-mono">{a.uhid}</div></TableCell>
                  <TableCell className="text-sm"><span className="font-mono font-semibold text-slate-700">{a.bed_number}</span><div className="text-xs text-slate-400">{a.ward || ""} {a.bed_type ? `· ${a.bed_type}` : ""}</div></TableCell>
                  <TableCell className="text-sm text-slate-600">{a.department || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.admitting_doctor_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.admitted_date}</TableCell>
                  {discharged && <TableCell className="text-sm text-slate-600">{a.discharged_at ? new Date(a.discharged_at).toLocaleDateString() : "—"}</TableCell>}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {discharged ? (
                      <div className="flex items-center justify-end gap-2" data-testid={`admission-invoice-${a.id}`}>
                        {a.invoice?.invoice_no && <span className="font-mono text-xs text-slate-500">{a.invoice.invoice_no}</span>}
                        <InvoiceBadge status={a.invoice_status || a.invoice?.status} />
                        <Button variant="outline" size="sm" onClick={() => navigate(`/app/ipd/admissions/${a.id}`)} data-testid={`view-record-${a.id}`}>View record</Button>
                      </div>
                    ) : (
                      has("ipd:edit") && <Button variant="outline" size="sm" onClick={() => discharge(a)} data-testid={`discharge-${a.id}`}><LogOut className="h-3.5 w-3.5 mr-1.5" /> Discharge</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={discharged ? 8 : 7} className="text-center text-slate-400 py-12">{discharged ? "No discharged patients yet." : 'No patients currently admitted. Click "Admit Patient" to get started.'}</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
